/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';

export interface DataStateErrorProps {
  /** Localized error copy shown to the user. */
  message: string;
  /** Retry handler; when omitted, no retry button is rendered. */
  onRetry?: () => void;
  /** Label for the retry button; defaults to the canonical Portuguese copy. */
  retryLabel?: string;
  /** Optional leading icon rendered above the message. */
  icon?: React.ReactNode;
  /** Test id applied to the outer container; preserves each surface's hook. */
  'data-testid'?: string;
}

const DEFAULT_RETRY_LABEL = 'Tentar novamente';

/**
 * Renders the optional outline retry button for the shared error visual.
 *
 * @param onRetry Retry handler; when absent, nothing is rendered.
 * @param label Localized retry label.
 * @returns The retry button element, or `null` when no handler is provided.
 */
function renderRetry(
  onRetry: (() => void) | undefined,
  label: string,
): React.ReactElement | null {
  if (!onRetry) return null;
  return (
    <button
      type="button"
      data-testid="data-state-retry"
      onClick={onRetry}
      className="rounded-(--radius-md) border border-(--seed-base) px-3 py-1.5 text-xs font-semibold text-(--seed-base) transition-colors hover:bg-(--seed-base)/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--seed-base) focus-visible:ring-offset-1"
    >
      {label}
    </button>
  );
}

/**
 * Canonical error visual shared across every data-dependent surface: a centered
 * column with an optional leading icon, the localized message and — only when an
 * `onRetry` handler is supplied — a `--seed-base` outline retry button. This is
 * the single source of truth that replaces the previously hand-rolled error cards.
 *
 * @param props Message, optional retry handler/label, optional icon and test id.
 * @returns The shared error visual element.
 */
export const DataStateError: React.FC<DataStateErrorProps> = ({
  message,
  onRetry,
  retryLabel = DEFAULT_RETRY_LABEL,
  icon,
  'data-testid': testId = 'data-state-error-visual',
}) => (
  <div
    data-testid={testId}
    className="flex flex-1 flex-col items-center justify-center gap-3"
  >
    {icon}
    <span className="text-center text-[13px] text-(--text-secondary)">{message}</span>
    {renderRetry(onRetry, retryLabel)}
  </div>
);
